import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, 
  FileText,
  MoreHorizontal,
  Download,
  Check,
  Clock,
  Eye,
  Filter
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoicesPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/pay`, {
        payment_method: 'bank_transfer',
        payment_date: new Date().toISOString()
      });
      toast.success('Facture marquée comme payée');
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredInvoices = invoices.filter(i => {
    const matchesSearch = i.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || i.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-700',
      requested: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    const labels = {
      pending: 'En attente',
      requested: 'Demandée',
      paid: 'Payée',
      rejected: 'Rejetée',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.pending)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const totalPending = invoices.filter(i => i.status === 'requested').reduce((acc, i) => acc + i.amount, 0);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Factures
            </h1>
            <p className="text-[#64748b] mt-1">
              {filteredInvoices.length} facture(s)
            </p>
          </div>
          
          {totalPending > 0 && (
            <div className="px-4 py-2 rounded-xl" style={{ backgroundColor: theme.colors?.alertLight }}>
              <p className="text-sm" style={{ color: theme.colors?.alert }}>
                <span className="font-bold">{totalPending.toLocaleString('fr-FR')}€</span> en attente de paiement
              </p>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher par numéro..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-default pl-10"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-default w-full md:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="requested">Demandées</SelectItem>
                  <SelectItem value="paid">Payées</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                Aucune facture trouvée
              </h3>
              <p className="text-[#64748b]">
                Les demandes de paiement des créatifs apparaîtront ici
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="text-left p-4">Numéro</th>
                    <th className="text-left p-4">Créatif</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Heures</th>
                    <th className="text-right p-4">Montant</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-left p-4">Date</th>
                    <th className="text-right p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-t border-[#e2e8f0] hover:bg-[#f8fafc]">
                      <td className="p-4">
                        <span className="font-mono font-medium" style={{ color: theme.colors?.primary }}>
                          {invoice.invoice_number}
                        </span>
                      </td>
                      <td className="p-4 text-[#64748b]">
                        {invoice.user_id?.slice(0, 8)}...
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">
                          {invoice.invoice_type === 'project' ? 'Projet' : 'Mensuel'}
                        </Badge>
                      </td>
                      <td className="p-4 font-mono">
                        {invoice.hours_worked?.toFixed(1)}h
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                          {invoice.amount?.toLocaleString('fr-FR')}€
                        </span>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="p-4 text-[#64748b] text-sm">
                        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger
                            </DropdownMenuItem>
                            {invoice.status === 'requested' && (
                              <DropdownMenuItem onClick={() => markAsPaid(invoice.id)}>
                                <Check className="w-4 h-4 mr-2" />
                                Marquer payée
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default InvoicesPage;
